import React, { useEffect, useState } from "react";
import { X, Loader2, Plus, Copy, KeyRound, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, getErr, safeCopy, toWaNumber } from "../lib/api";

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh","Chandigarh","Puducherry","Andaman and Nicobar Islands","Dadra and Nagar Haveli and Daman and Diu","Lakshadweep",
];

const STATUSES = ["New", "Contacted", "In Progress", "Pending", "Completed"];

/**
 * Reusable modal used by both AdminLeads and AdminCustomers to create a record.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - kind: "lead" | "customer"
 *  - onCreated: (record) => void  // called after successful create
 */
export default function AddRecordDialog({ open, onClose, kind, onCreated }) {
  const [services, setServices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setCreated(null);
    api.get("/admin/services").then((r) => setServices(r.data.services || [])).catch(() => {
      // Fall back to public catalog if admin endpoint fails (e.g., during preview)
      api.get("/services").then((r) => setServices(r.data.services || [])).catch(() => {});
    });
    api.get("/admin/operators").then((r) => setOperators(r.data.operators || [])).catch(() => {});
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e?.preventDefault();
    if (!form.full_name?.trim()) return toast.error("Full name is required");
    if (!form.mobile || form.mobile.replace(/\D/g, "").length < 10) return toast.error("Enter a valid 10-digit mobile");
    if (!form.service_id) return toast.error("Pick a service");
    setSubmitting(true);
    try {
      const url = kind === "customer" ? "/admin/customers" : "/admin/leads";
      const body = { ...form };
      if (kind === "customer") {
        body.initial_paid = Number(body.initial_paid) || 0;
        delete body.status;
      } else {
        delete body.initial_paid;
        delete body.initial_paid_method;
      }
      const { data } = await api.post(url, body);
      setCreated(data);
      toast.success(`${kind === "customer" ? "Customer" : "Lead"} created — ${data.ref}`);
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(getErr(err, `Failed to create ${kind}`));
    } finally {
      setSubmitting(false);
    }
  };

  const onCopyCreds = async () => {
    if (!created?.credentials) return;
    const text = `Login ID: ${created.credentials.login_id}\nPassword: ${created.credentials.password}\nLogin at: ${window.location.origin}${created.credentials.portal_url}`;
    const ok = await safeCopy(text);
    ok ? toast.success("Credentials copied") : toast.error("Copy failed");
  };

  const onShareWA = () => {
    if (!created?.credentials) return;
    const wa = toWaNumber(form.mobile);
    if (!wa) return toast.error("Invalid mobile");
    const msg = `Hi ${form.full_name},\n\nWelcome to *FinSahayak* 🙏\n\nReference: ${created.ref}\n\n*Your portal access:*\nLogin ID: ${created.credentials.login_id}\nPassword: ${created.credentials.password}\nLogin at: ${window.location.origin}${created.credentials.portal_url}\n\nFor any help, just reply here.\n— Team FinSahayak\nfinsahayak.com`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const title = kind === "customer" ? "Add customer" : "Add lead";
  const subtitle = kind === "customer"
    ? "Skip the lead stage — create a customer directly. Portal credentials will be auto-generated."
    : "Manually capture a new enquiry. The lead will appear in your CRM with auto-generated portal credentials.";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 backdrop-blur-sm p-3" data-testid={`add-${kind}-dialog`}>
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E2DC] px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <span className="label-eyebrow text-[10px]">CRM</span>
            <h2 className="font-display text-xl font-medium text-[#121C17] mt-0.5">{title}</h2>
            <p className="text-xs text-[#5C6661] mt-0.5">{subtitle}</p>
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-[#F3F1EC]" data-testid={`add-${kind}-close`} aria-label="Close"><X size={18} /></button>
        </div>

        {created ? (
          <CreatedSuccess kind={kind} created={created} form={form} onCopy={onCopyCreds} onShare={onShareWA} onClose={onClose} />
        ) : (
          <form onSubmit={onSubmit} className="px-6 py-5 space-y-5" data-testid={`add-${kind}-form`}>
            <Section title="Identity">
              <Grid>
                <Field label="Full name *" v={form.full_name} on={(v) => set("full_name", v)} test={`add-${kind}-full_name`} />
                <Field label="Mobile *" v={form.mobile} on={(v) => set("mobile", v.replace(/\D/g, "").slice(0, 10))} test={`add-${kind}-mobile`} placeholder="10-digit" />
                <Field label="WhatsApp number" v={form.whatsapp} on={(v) => set("whatsapp", v.replace(/\D/g, "").slice(0, 10))} test={`add-${kind}-whatsapp`} placeholder="Defaults to mobile" />
                <Field label="Email" v={form.email} on={(v) => set("email", v)} test={`add-${kind}-email`} type="email" />
                <Field label="PAN" v={form.pan} on={(v) => set("pan", v.toUpperCase().slice(0, 10))} test={`add-${kind}-pan`} mono />
                <Field label="Aadhaar" v={form.aadhaar} on={(v) => set("aadhaar", v.replace(/\D/g, "").slice(0, 12))} test={`add-${kind}-aadhaar`} mono />
              </Grid>
            </Section>

            <Section title="Service">
              <Grid>
                <SelectField label="Service *" v={form.service_id} on={(v) => set("service_id", v)} test={`add-${kind}-service`}>
                  <option value="">— Pick a service —</option>
                  {services.filter((s) => s.is_active !== false).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · ₹{(s.price || 0).toLocaleString("en-IN")}
                    </option>
                  ))}
                </SelectField>
                <SelectField label="Assign to operator" v={form.assigned_to} on={(v) => set("assigned_to", v)} test={`add-${kind}-assignee`}>
                  <option value="">— Unassigned —</option>
                  {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </SelectField>
                {kind === "lead" ? (
                  <SelectField label="Status" v={form.status} on={(v) => set("status", v)} test={`add-${kind}-status`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </SelectField>
                ) : (
                  <>
                    <Field label="Initial paid (₹)" v={form.initial_paid} on={(v) => set("initial_paid", v.replace(/[^\d.]/g, ""))} test={`add-${kind}-initial_paid`} placeholder="0" />
                    <SelectField label="Payment method" v={form.initial_paid_method} on={(v) => set("initial_paid_method", v)} test={`add-${kind}-paid_method`}>
                      {["Cash", "UPI", "Bank Transfer", "Cheque", "Card", "Razorpay"].map((m) => <option key={m} value={m}>{m}</option>)}
                    </SelectField>
                  </>
                )}
              </Grid>
            </Section>

            <Section title="Address (optional)" subtitle="Address fields can be added later from the detail page.">
              <Grid>
                <SelectField label="State" v={form.state} on={(v) => set("state", v)} test={`add-${kind}-state`}>
                  <option value="">— Select —</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </SelectField>
                <Field label="District" v={form.district} on={(v) => set("district", v)} test={`add-${kind}-district`} />
                <Field label="City" v={form.city} on={(v) => set("city", v)} test={`add-${kind}-city`} />
                <Field label="PIN code" v={form.pincode} on={(v) => set("pincode", v.replace(/\D/g, "").slice(0, 6))} test={`add-${kind}-pincode`} />
              </Grid>
              <div className="mt-3">
                <Field label="Address" v={form.address} on={(v) => set("address", v)} test={`add-${kind}-address`} />
              </div>
            </Section>

            {kind === "lead" && (
              <Section title="Notes">
                <textarea
                  value={form.remarks}
                  onChange={(e) => set("remarks", e.target.value)}
                  data-testid={`add-${kind}-remarks`}
                  rows={2}
                  placeholder="Anything to remember about this lead…"
                  className="w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 text-sm focus:border-[#1E4D3B]"
                />
              </Section>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-[#E5E2DC]">
              <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]" data-testid={`add-${kind}-cancel`}>Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60" data-testid={`add-${kind}-submit`}>
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {submitting ? "Creating…" : `Create ${kind}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function emptyForm() {
  return {
    full_name: "", mobile: "", whatsapp: "", email: "",
    pan: "", aadhaar: "",
    state: "", district: "", city: "", address: "", pincode: "",
    service_id: "", assigned_to: "",
    status: "New", remarks: "",
    initial_paid: "", initial_paid_method: "Cash",
  };
}

const Section = ({ title, subtitle, children }) => (
  <div>
    <div className="mb-3">
      <h3 className="label-eyebrow text-[10px]">{title}</h3>
      {subtitle && <p className="text-xs text-[#5C6661] mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;

const Field = ({ label, v, on, test, type = "text", placeholder, mono }) => (
  <label className="block">
    <span className="text-xs text-[#5C6661]">{label}</span>
    <input
      type={type}
      value={v || ""}
      onChange={(e) => on(e.target.value)}
      placeholder={placeholder}
      data-testid={test}
      className={`mt-1 w-full bg-white border border-[#E5E2DC] rounded-xl px-4 py-2.5 text-sm focus:border-[#1E4D3B] ${mono ? "font-mono" : ""}`}
    />
  </label>
);

const SelectField = ({ label, v, on, test, children }) => (
  <label className="block">
    <span className="text-xs text-[#5C6661]">{label}</span>
    <select
      value={v || ""}
      onChange={(e) => on(e.target.value)}
      data-testid={test}
      className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]"
    >
      {children}
    </select>
  </label>
);

function CreatedSuccess({ kind, created, form, onCopy, onShare, onClose }) {
  return (
    <div className="px-6 py-7 text-center" data-testid={`add-${kind}-success`}>
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 grid place-items-center text-emerald-700">
        <CheckCircle2 size={28} />
      </div>
      <h3 className="mt-4 font-display text-xl text-[#121C17]">
        {kind === "customer" ? "Customer" : "Lead"} created
      </h3>
      <p className="text-sm text-[#5C6661]">
        Reference <span className="font-mono text-[#121C17]">{created.ref}</span> · share the portal credentials below.
      </p>

      <div className="mt-5 bg-[#F3F1EC] rounded-2xl p-4 text-left max-w-md mx-auto" data-testid={`add-${kind}-creds`}>
        <div className="flex items-center gap-2 text-xs label-eyebrow text-[#5C6661]"><KeyRound size={12} /> Portal credentials</div>
        <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
          <span className="text-[#5C6661]">Login ID</span>
          <span className="font-mono text-[#121C17]">{created.credentials.login_id}</span>
          <span className="text-[#5C6661]">Password</span>
          <span className="font-mono text-[#121C17]">{created.credentials.password}</span>
          <span className="text-[#5C6661]">Login URL</span>
          <a className="text-[#1E4D3B] underline truncate" href={created.credentials.portal_url} target="_blank" rel="noopener noreferrer">{window.location.origin}{created.credentials.portal_url}</a>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <button onClick={onCopy} data-testid={`add-${kind}-copy`} className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] inline-flex items-center gap-1.5 hover:bg-[#F3F1EC]"><Copy size={13} /> Copy</button>
        <button onClick={onShare} data-testid={`add-${kind}-share-wa`} className="rounded-full px-4 py-2 text-sm font-medium text-white inline-flex items-center gap-1.5" style={{ background: "#25D366" }}><MessageCircle size={13} /> Share on WhatsApp</button>
        <a href={kind === "customer" ? `/admin/customers/${created.id}` : `/admin/leads/${created.id}`} data-testid={`add-${kind}-open-detail`} className="btn-primary rounded-full px-4 py-2 text-sm font-medium">Open detail →</a>
        <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-medium border border-[#E5E2DC] hover:bg-[#F3F1EC]" data-testid={`add-${kind}-close-after`}>Done</button>
      </div>
    </div>
  );
}
